import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wallet, ArrowUp, User, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { publicApi } from '@/lib/api';

const ScanInfo = () => {
  const { qrData } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const fetchScanInfo = async () => {
    setLoading(true);
    setError(null);
    setIsNetworkError(false);
    try {
      const response = await publicApi.post('/public/scan-info', { qr_data: qrData.trim() });
      setData(response.data);
    } catch (err) {
      if (err.response) {
        // Backend returned an error (400, 404, etc.)
        setError(err.response.data?.detail || 'Invalid QR code');
      } else {
        // Network error (CORS, connectivity, DNS)
        setError('Unable to connect to server. Please check your connection and try again.');
        setIsNetworkError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScanInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 text-red-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Invalid QR Code</h2>
            <p className="text-gray-500">{error}</p>
            {isNetworkError && (
              <Button variant="outline" className="mt-4" onClick={fetchScanInfo}>
                <RefreshCw className="h-4 w-4 mr-2" /> Retry
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm p-4">
        <div className="max-w-md mx-auto">
          <p className="text-sm text-gray-500">{data.event_name}</p>
          <h1 className="font-bold text-lg">{data.attendee_name}</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Attendee Info Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-7 w-7 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{data.attendee_name}</h2>
                {data.category && (
                  <span className="inline-block px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 mt-1">
                    {data.category}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {data.check_in_status === 'checked_in' ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Checked In</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-gray-400" />
                  <span>Not Checked In</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Wallet Section */}
        {data.wallet_enabled && data.wallet_token && (
          <>
            <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
              <CardContent className="p-6 text-center">
                <Wallet className="h-8 w-8 mx-auto mb-2 opacity-80" />
                <p className="text-blue-200 text-sm">Wallet Balance</p>
                <p className="text-4xl font-bold mt-1">{formatCurrency(data.wallet_balance)}</p>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Link to={`/wallet/${data.wallet_token}/top-up`} className="flex-1">
                <Button className="w-full h-12" size="lg">
                  <ArrowUp className="h-5 w-5 mr-2" /> Top Up
                </Button>
              </Link>
              <Link to={`/wallet/${data.wallet_token}`} className="flex-1">
                <Button variant="outline" className="w-full h-12" size="lg">
                  <Wallet className="h-5 w-5 mr-2" /> View Wallet
                </Button>
              </Link>
            </div>
          </>
        )}

        {data.wallet_enabled && !data.wallet_token && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center text-sm text-amber-700">
              Wallet is enabled for this event but not yet set up for your account.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ScanInfo;
